import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import { SectionCard } from "./SectionCard";
import { SettingsSection } from "./SettingsSection";
import type { ClientManagementState } from "../types";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import type { ClientJobRole } from "../api/clientManagement";

interface JobRolesTabProps {
  effectiveClientId: string | null;
  managementState: ClientManagementState;
  onChange: (field: keyof ClientManagementState, value: string | boolean) => void;
  createJobRoleMutation: UseMutationResult<unknown, unknown, { name: string; is_active: boolean }>;
  updateJobRoleMutation: UseMutationResult<
    unknown,
    unknown,
    { jobRoleId: string; payload: { name?: string; is_active?: boolean } }
  >;
  deleteJobRoleMutation: UseMutationResult<unknown, unknown, string>;
  jobRolesQuery: UseQueryResult<{ data: ClientJobRole[] }>;
}

export const JobRolesTab: React.FC<JobRolesTabProps> = ({
  effectiveClientId,
  managementState,
  onChange,
  createJobRoleMutation,
  updateJobRoleMutation,
  deleteJobRoleMutation,
  jobRolesQuery,
}) => {
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState("");
  const [addActive, setAddActive] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editActive, setEditActive] = useState(true);

  const roles = jobRolesQuery.data?.data ?? [];
  const filtered = roles.filter((r) =>
    r.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleOpenAdd = () => {
    setAddName("");
    setAddActive(true);
    setAddOpen(true);
  };

  const handleConfirmAdd = () => {
    if (!addName.trim() || !effectiveClientId) return;
    createJobRoleMutation.mutate(
      { name: addName.trim(), is_active: addActive },
      { onSuccess: () => setAddOpen(false) }
    );
    onChange("jobRoleName", addName.trim());
  };

  const handleStartEdit = (role: ClientJobRole) => {
    setEditingId(role.id);
    setEditName(role.name);
    setEditActive(role.is_active !== false);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName("");
  };

  const handleSaveEdit = (id: string) => {
    updateJobRoleMutation.mutate(
      { jobRoleId: id, payload: { name: editName, is_active: editActive } },
      { onSuccess: () => handleCancelEdit() }
    );
  };

  return (
    <SettingsSection>
      {!effectiveClientId && (
        <Alert severity="info">Select a client to manage job roles.</Alert>
      )}

      <SectionCard
        title="Job roles"
        description={`${roles.length} role${roles.length === 1 ? "" : "s"} configured`}
        action={
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            disabled={!effectiveClientId}
            onClick={handleOpenAdd}
          >
            Add role
          </Button>
        }
      >
        {/* Search bar */}
        <TextField
          size="small"
          fullWidth
          placeholder="Search job roles…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: "text.disabled" }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{ mb: 2 }}
        />

        <Divider />

        {/* List */}
        {jobRolesQuery.isLoading ? (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">Loading job roles…</Typography>
          </Stack>
        ) : roles.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
            <WorkOutlineIcon sx={{ fontSize: 36, opacity: 0.3, mb: 1 }} />
            <Typography variant="body2">No job roles yet.</Typography>
            <Typography variant="caption">Click "Add role" to create one.</Typography>
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ py: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No job roles match "{search}"
            </Typography>
          </Box>
        ) : (
          <Stack divider={<Divider />} spacing={0}>
            {filtered.map((role) => {
              const isEditing = editingId === role.id;
              const isActive = role.is_active !== false;

              return (
                <Box key={role.id} sx={{ py: 1.25 }}>
                  {isEditing ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TextField
                        size="small"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && editName.trim()) handleSaveEdit(role.id);
                          if (e.key === "Escape") handleCancelEdit();
                        }}
                        sx={{ flex: 1 }}
                      />
                      <Chip
                        label={editActive ? "Active" : "Inactive"}
                        size="small"
                        color={editActive ? "success" : "default"}
                        variant={editActive ? "filled" : "outlined"}
                        onClick={() => setEditActive((v) => !v)}
                        sx={{ cursor: "pointer" }}
                      />
                      <Tooltip title="Save">
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            disabled={!editName.trim() || updateJobRoleMutation.isPending}
                            onClick={() => handleSaveEdit(role.id)}
                          >
                            {updateJobRoleMutation.isPending ? (
                              <CircularProgress size={16} />
                            ) : (
                              <CheckIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Cancel">
                        <IconButton size="small" onClick={handleCancelEdit}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ) : (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {role.name}
                      </Typography>
                      <Chip
                        label={isActive ? "Active" : "Inactive"}
                        size="small"
                        color={isActive ? "success" : "default"}
                        variant="outlined"
                      />
                      <Tooltip title="Rename">
                        <IconButton size="small" onClick={() => handleStartEdit(role)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          disabled={deleteJobRoleMutation.isPending}
                          onClick={() => {
                            if (window.confirm("Delete this job role? This cannot be undone.")) {
                              deleteJobRoleMutation.mutate(role.id);
                            }
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </SectionCard>

      {/* Add dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add job role</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              size="small"
              fullWidth
              label="Job role name"
              value={addName}
              onChange={(e) => setAddName(e.target.value)}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter" && addName.trim()) handleConfirmAdd();
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={addActive}
                  onChange={(e) => setAddActive(e.target.checked)}
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!addName.trim() || createJobRoleMutation.isPending}
            onClick={handleConfirmAdd}
          >
            {createJobRoleMutation.isPending ? <CircularProgress size={18} /> : "Add role"}
          </Button>
        </DialogActions>
      </Dialog>
    </SettingsSection>
  );
};
